import { ReactElement, ReactNode } from "react";
import { Col, Flex, Row } from "antd";
import {
  AppstoreOutlined,
  CalendarOutlined,
  CarryOutOutlined,
  ClockCircleOutlined,
  GiftOutlined,
  LineChartOutlined,
  PieChartOutlined,
  RiseOutlined,
  ShoppingOutlined,
  SolutionOutlined,
  SwapOutlined,
  TagOutlined,
  TagsOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import PageHeading from "components/PageHeading.component";
import { HubGroup, HubRow } from "components/HubGroup.component";
import { translate } from "shared/languages";
import PATHS from "shared/constants/paths";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";

// Every Laporan report on one page, grouped exactly as the old tabs were.
// Replaces the seven grid pages and the tab bar: reading down the left column
// then the right gives the old tab order, so nothing has to be relearned.
//
// Same grouped-list pattern as the Pengaturan hub, from the same component.
// Two deliberate differences:
//
// 1. A report has no current value to show, so rows pass reserveMeta={false}
//    rather than holding an empty value column open.
// 2. Every chip is neutral. Pengaturan tints two of its five groups because
//    its rows are heterogeneous (a policy, a tax rate, a language picker), so
//    a hue helps you find the money settings in a wall of unrelated toggles.
//    Laporan's rows are homogeneous: all 23 are a report about a subject, and
//    the heading above them already names that subject, so a per-group hue
//    would encode the section a second time.
//
//    A per-ROW hue was tried first (green on the reports the financial
//    restriction removes) and dropped 2026-10-03. It split a group visually,
//    Klien read grey/green/grey, and it encoded a permissions fact the viewer
//    cannot act on from this page: the restricted Admin never sees green,
//    because those rows are not rendered for them, so its only audience was a
//    Manager who changes that setting in Pengaturan. Without a legend green
//    also reads as "money" or "important" rather than "restricted", and green
//    already means active/completed/configured in StatusTone.
//
//    Structure is carried by the seven headings, two columns, fifteen
//    distinct icon shapes and the chevrons, which is what Pengaturan's own
//    three neutral sections do. If the page ever reads as flat, tint Reward
//    dan Promosi amber and nothing else: four rows is enough to read as a
//    block, and that group has an identically named counterpart on
//    Pengaturan which is already amber. Do not reintroduce a tone that
//    depends on a capability.

type ReportRow = {
  /**
   * Names the report TYPE, not the subject: inside a group the heading
   * already says Paket or Klien, so repeating that in the icon leaves two
   * rows looking identical. Paket and Membership therefore carry the same
   * six icons in the same order.
   */
  icon: ReactElement;
  title: string;
  description: string;
  to: string;
  /** Hidden when the admin lacks this capability; the route is guarded too. */
  capability?: AdminCapability;
};

type ReportGroup = {
  label: string;
  /**
   * Stable identity, independent of language, for the two layout rules that
   * single a group out: `reward` moves last for a restricted Admin, and
   * `package` / `membership` are cut as one block (see blockOf). Matching on
   * `label` instead would compare a translated string, which breaks the
   * moment two groups share wording or a key goes missing and translate()
   * returns the key itself.
   */
  key?: "reward" | "package" | "membership";
  rows: ReportRow[];
};

// Built per render, never hoisted to a module constant: translate() is a bare
// alias of i18next.t and is not reactive. LanguageRoot handles a language
// switch by remounting the tree, which re-runs this function but NOT module
// initialisation, so a hoisted array would freeze every label in whichever
// language the page first loaded in.
const buildGroups = (): ReportGroup[] => {
  const money = AdminCapability.FinancialAnalytics;
  return [
    {
      label: translate("analyticsPage.group_summary"),
      rows: [
        {
          icon: <LineChartOutlined />,
          title: translate("analyticsPage.sales.report_trend"),
          description: translate("analyticsPage.sales.report_trend_desc"),
          to: PATHS.ANALYTICS_SALES_TREND,
          capability: money,
        },
      ],
    },
    {
      label: translate("analyticsPage.tab.class"),
      rows: [
        {
          icon: <PieChartOutlined />,
          title: translate("analyticsPage.class.report_performance"),
          description: translate("analyticsPage.class.report_performance_desc"),
          to: PATHS.ANALYTICS_CLASS_PERFORMANCE,
        },
        {
          icon: <CalendarOutlined />,
          title: translate("analyticsPage.sales.report_services"),
          description: translate("analyticsPage.sales.report_services_desc"),
          to: PATHS.ANALYTICS_SALES_SERVICES,
          capability: money,
        },
      ],
    },
    {
      label: translate("analyticsPage.tab.client"),
      rows: [
        {
          icon: <TeamOutlined />,
          title: translate("analyticsPage.client.report_performance"),
          description: translate("analyticsPage.client.report_performance_desc"),
          to: PATHS.ANALYTICS_CLIENT_PERFORMANCE,
        },
        {
          icon: <RiseOutlined />,
          title: translate("analyticsPage.sales.report_clients"),
          description: translate("analyticsPage.sales.report_clients_desc"),
          to: PATHS.ANALYTICS_SALES_CLIENTS,
          capability: money,
        },
        {
          icon: <ClockCircleOutlined />,
          title: translate("analyticsPage.client.report_inactive"),
          description: translate("analyticsPage.client.report_inactive_desc"),
          to: PATHS.ANALYTICS_CLIENT_INACTIVE,
        },
      ],
    },
    {
      label: translate("analyticsPage.tab.instructor"),
      rows: [
        {
          icon: <SolutionOutlined />,
          title: translate("analyticsPage.instructor.report_performance"),
          description: translate("analyticsPage.instructor.report_performance_desc"),
          to: PATHS.ANALYTICS_INSTRUCTOR_PERFORMANCE,
        },
      ],
    },
    {
      // Every report here is open to any Admin, including Performa Kode
      // Diskon, which carries revenue (Andrew, 2026-09-24): like Loyalty and
      // Referral, it answers how a programme performed.
      label: translate("analyticsPage.tab.reward"),
      key: "reward",
      rows: [
        {
          icon: <GiftOutlined />,
          title: translate("analyticsPage.loyalty.report_performance"),
          description: translate("analyticsPage.loyalty.report_performance_desc"),
          to: PATHS.ANALYTICS_REWARD_LOYALTY,
        },
        {
          icon: <UsergroupAddOutlined />,
          title: translate("analyticsPage.referral.report_performance"),
          description: translate("analyticsPage.referral.report_performance_desc"),
          to: PATHS.ANALYTICS_REWARD_REFERRAL,
        },
        {
          icon: <CarryOutOutlined />,
          title: translate("analyticsPage.rewardUsage.report_title"),
          description: translate("analyticsPage.rewardUsage.report_desc"),
          to: PATHS.ANALYTICS_REWARD_USAGE,
        },
        {
          icon: <TagOutlined />,
          title: translate("analyticsPage.discountCode.report_title"),
          description: translate("analyticsPage.discountCode.report_desc"),
          to: PATHS.ANALYTICS_REWARD_DISCOUNT_CODES,
        },
      ],
    },
    {
      label: translate("analyticsPage.tab.package"),
      key: "package",
      rows: [
        {
          icon: <AppstoreOutlined />,
          title: translate("analyticsPage.package.report_overview"),
          description: translate("analyticsPage.package.report_overview_desc"),
          to: PATHS.ANALYTICS_PACKAGE_OVERVIEW,
        },
        {
          icon: <RiseOutlined />,
          title: translate("analyticsPage.package.report_pendapatan_produk"),
          description: translate("analyticsPage.package.report_pendapatan_produk_desc"),
          to: PATHS.ANALYTICS_SALES_PACKAGES,
          capability: money,
        },
        {
          icon: <TagsOutlined />,
          title: translate("analyticsPage.package.report_pendapatan_produk_promo"),
          description: translate("analyticsPage.package.report_pendapatan_produk_promo_desc"),
          to: PATHS.ANALYTICS_SALES_PACKAGES_PROMO,
          capability: money,
        },
        {
          icon: <ShoppingOutlined />,
          title: translate("analyticsPage.package.report_purchases"),
          description: translate("analyticsPage.package.report_purchases_desc"),
          to: PATHS.ANALYTICS_PACKAGE_PURCHASES,
          capability: money,
        },
        {
          icon: <SwapOutlined />,
          title: translate("analyticsPage.package.report_compare"),
          description: translate("analyticsPage.package.report_compare_desc"),
          to: PATHS.ANALYTICS_PACKAGE_COMPARE,
          capability: money,
        },
        {
          icon: <ClockCircleOutlined />,
          title: translate("analyticsPage.package.report_status"),
          description: translate("analyticsPage.package.report_status_desc"),
          to: PATHS.ANALYTICS_PACKAGE_EXPIRY,
        },
      ],
    },
    {
      label: translate("analyticsPage.tab.membership"),
      key: "membership",
      rows: [
        {
          icon: <AppstoreOutlined />,
          title: translate("analyticsPage.membership.report_overview"),
          description: translate("analyticsPage.membership.report_overview_desc"),
          to: PATHS.ANALYTICS_MEMBERSHIP_OVERVIEW,
        },
        {
          icon: <RiseOutlined />,
          title: translate("analyticsPage.membership.report_pendapatan_produk"),
          description: translate("analyticsPage.membership.report_pendapatan_produk_desc"),
          to: PATHS.ANALYTICS_SALES_MEMBERSHIPS,
          capability: money,
        },
        {
          icon: <TagsOutlined />,
          title: translate("analyticsPage.membership.report_pendapatan_produk_promo"),
          description: translate("analyticsPage.membership.report_pendapatan_produk_promo_desc"),
          to: PATHS.ANALYTICS_SALES_MEMBERSHIPS_PROMO,
          capability: money,
        },
        {
          icon: <ShoppingOutlined />,
          title: translate("analyticsPage.membership.report_purchases"),
          description: translate("analyticsPage.membership.report_purchases_desc"),
          to: PATHS.ANALYTICS_MEMBERSHIP_PURCHASES,
          capability: money,
        },
        {
          icon: <SwapOutlined />,
          title: translate("analyticsPage.membership.report_compare"),
          description: translate("analyticsPage.membership.report_compare_desc"),
          to: PATHS.ANALYTICS_MEMBERSHIP_COMPARE,
          capability: money,
        },
        {
          icon: <ClockCircleOutlined />,
          title: translate("analyticsPage.membership.report_status"),
          description: translate("analyticsPage.membership.report_status_desc"),
          to: PATHS.ANALYTICS_MEMBERSHIP_EXPIRY,
        },
      ],
    },
  ];
};

// A heading costs about half a row's height, so a group weighs rows + 0.5.
const weigh = (group: ReportGroup) => group.rows.length + 0.5;

/**
 * Which groups refuse to be split across columns. Paket and Membership are a
 * matched pair, the same six reports once per product type: splitting them
 * puts Membership at the top of the right column while Paket sits at the
 * bottom of the left, so Membership reads first even though the reading order
 * is correct.
 *
 * Every other group is its own block, keyed on the group OBJECT rather than
 * its label. A label is a translated string, so two groups whose wording ever
 * collided (or two keys that both went missing and resolved to themselves)
 * would silently be glued into one block.
 */
const blockOf = (group: ReportGroup): unknown =>
  group.key === "package" || group.key === "membership" ? "product" : group;

/**
 * Splits the groups into two columns at the point nearest half the total
 * height. A contiguous cut, so reading down the left column then the right
 * always gives the original order.
 *
 * Pengaturan hardcodes its two columns; Laporan cannot, because a restricted
 * Admin loses a whole group (Pendapatan) plus four rows, which would leave a
 * fixed left column far shorter than the right.
 */
const splitColumns = (groups: ReportGroup[]): [ReportGroup[], ReportGroup[]] => {
  const total = groups.reduce((sum, g) => sum + weigh(g), 0);
  let running = 0;
  let cut = groups.length;
  let i = 0;
  while (i < groups.length) {
    // A block is decided as a whole, so weigh every group in it before asking
    // whether to cut. Weighing only its first group understates what moving it
    // across costs and lands the cut one block too late.
    let end = i + 1;
    while (end < groups.length && blockOf(groups[end]) === blockOf(groups[i])) end += 1;
    const next = groups.slice(i, end).reduce((sum, g) => sum + weigh(g), running);
    // Stop where moving this block across stops improving the balance.
    if (Math.abs(next - total / 2) > Math.abs(running - total / 2)) {
      cut = i;
      break;
    }
    running = next;
    i = end;
  }
  // Never leave the left column empty: a single group belongs full width.
  if (cut === 0) cut = groups.length;
  return [groups.slice(0, cut), groups.slice(cut)];
};

// Half width beside a sibling, full width alone: a single surviving group
// should fill the page rather than sit in a half with dead space beside it.
const Column = ({ children, full }: { children: ReactNode; full?: boolean }) => (
  <Col xs={24} xl={full ? 24 : 12}>
    <Flex vertical gap={24}>
      {children}
    </Flex>
  </Col>
);

const AnalyticsIndexPage = () => {
  // isLoading, never a query's isPending: useBusinessSettings is disabled for
  // Managers and a disabled query stays pending forever. The hook already
  // masks that. Reading capabilities only through it also keeps a system
  // admin from issuing a business-settings request they would be signed out
  // for.
  const { isRestricted, isLoading } = useAdminCapabilities();

  // isCapabilityRestricted fails CLOSED while the flags are in flight, so
  // rendering during load would show the reduced page and then expand it,
  // re-splitting both columns. Every plain Admin would see that, not just
  // restricted ones. The heading stays so the page is never blank.
  const groupsAll = isLoading
    ? []
    : buildGroups()
        .map((group) => ({
          ...group,
          rows: group.rows.filter((row) => !row.capability || !isRestricted(row.capability)),
        }))
        .filter((group) => group.rows.length > 0);

  // Reward dan Promosi moves last for a restricted Admin, and ONLY for them.
  //
  // For that audience it costs nothing: with Paket and Membership cut as one
  // block the columns land 4/8 either way (222px trailing gap, measured at
  // 1600px wide), so this only decides whether Reward sits above or below the
  // product sections in the right column. Below matches the old tab order and
  // Pengaturan, which also puts Promosi & Reward late.
  //
  // Applying it to everyone is NOT free: on the full page the product block is
  // 12 rows, and putting Reward in front of it is what lets the cut land
  // 11/12 (77px). Reward last there gives 7/16. Hence the condition.
  //
  // Re-measure rather than reason about it if the row counts change.
  const groups =
    groupsAll.length > 0 && isRestricted(AdminCapability.FinancialAnalytics)
      ? [
          ...groupsAll.filter((group) => group.key !== "reward"),
          ...groupsAll.filter((group) => group.key === "reward"),
        ]
      : groupsAll;

  const [left, right] = splitColumns(groups);

  const renderGroup = (group: ReportGroup) => (
    <HubGroup key={group.label} label={group.label}>
      {group.rows.map((row) => (
        <HubRow
          key={row.to}
          // Uniformly neutral, by decision. See the note at the top of this
          // file before giving any row or group its own hue.
          tone="neutral"
          icon={row.icon}
          title={row.title}
          description={row.description}
          to={row.to}
          reserveMeta={false}
        />
      ))}
    </HubGroup>
  );

  return (
    <>
      <PageHeading title={translate("analyticsPage.title")} />
      <Row gutter={[24, 24]} style={{ marginTop: 8 }}>
        <Column full={right.length === 0}>{left.map(renderGroup)}</Column>
        {right.length > 0 && <Column>{right.map(renderGroup)}</Column>}
      </Row>
    </>
  );
};

export default AnalyticsIndexPage;
